import * as React from 'react';

import {
  InlineEditorCancelButton,
  InlineEditorLogic_EditMode,
  InlineEditorSaveButton
} from '@tableau/vizportalclient-common-components';
import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~common/react/CommonStyles';
import { cssClass, flexColumnStyle, flexRowStyle, Style } from '~src/style';

import { ACTION_DIALOG_TESTID } from './ContentDetailsDialog';


const SECTION_CLASS_PREFIX = 'content-detail-section';

const section = cssClass(SECTION_CLASS_PREFIX, 'section', {
  ...flexRowStyle,
  width: '100%',
  lineHeight: CommonStyles.Sizes.RowHeight+'px',
});

const commonPadding: Style = {
  paddingBottom: TabStyles.Spacing.MediumSpace,
  paddingTop: TabStyles.Spacing.MediumSpace,
};

const sectionDivider: Style = {
  borderBottom: `1px solid ${CommonStyles.Colors.DividerLineColor}`
};

const header = cssClass(SECTION_CLASS_PREFIX, 'header', {
  ...commonPadding,
  textAlign: 'right',
  fontSize: '15px',
  fontWeight: 'normal',
  color: CommonStyles.Colors.GreyTextColor,
  width: '15%',
  paddingRight: TabStyles.Spacing.MediumSpace,
  flexShrink: 0,
});

const detail = cssClass(SECTION_CLASS_PREFIX, 'detail', {
  ...commonPadding,
  ...sectionDivider,
  flexGrow: 1,
  flexShrink: 1,
  paddingLeft: TabStyles.Spacing.DefaultSpace,
  overflowX: 'auto',
});

const action = cssClass(SECTION_CLASS_PREFIX, 'action', {
  ...flexColumnStyle,
  ...commonPadding,
  ...sectionDivider,
  paddingLeft: TabStyles.Spacing.MediumSpace,
});

export const sectionContainer = cssClass(ACTION_DIALOG_TESTID, 'section-container', {
  $nest: {
      [`&>*:last-child > [class^="${detail}"]`] : {
        borderBottom: 'none'
      },
      [`&>*:last-child > [class^="${action}"]`] : {
        borderBottom: 'none'
      },
  }
});

const TEST_ID_PREFIX = 'content-detail-section-';

export interface DetailSectionProps {
  readonly header: string;
  readonly headerId?: string;
  readonly detail: JSX.Element | string;
  readonly isActionAllowed?: boolean;
  readonly action?: JSX.Element;
  readonly testId: string;
}

export function ContentDetailSection(props: DetailSectionProps): JSX.Element {
  const sectionTestId = TEST_ID_PREFIX + (props.testId || '');
  return (
    <div className={section} data-tb-test-id={sectionTestId}>
      <header className={header} id={props.headerId} data-tb-test-id={sectionTestId+'-header'}>
        {props.header}
      </header>
      <div className={detail} data-tb-test-id={sectionTestId+'-detail'}>
        {props.detail}
      </div>
      {props.isActionAllowed && <div className={action} data-tb-test-id={sectionTestId+'-action'}>
        {props.action}
      </div>}
    </div>
  );
}


export interface DetailSaveCancelButtonsProps {
  readonly inlineEditorLogic: InlineEditorLogic_EditMode<unknown>;
}

export function DetailSaveCancelButtons(props: DetailSaveCancelButtonsProps): JSX.Element {
  return (
    <>
      <div style={{ marginBottom: TabStyles.Spacing.DefaultSpace }}>
        { <InlineEditorSaveButton {...props.inlineEditorLogic} /> }
      </div>
      { <InlineEditorCancelButton {...props.inlineEditorLogic} /> }
    </>
  );
}


export interface DetailEditButtonProps {
  readonly buttonLabel: string;
  readonly buttonTestId: string;
  readonly onClickAction: () => void;
}

export function DetailEditButton(props: DetailEditButtonProps): JSX.Element {
  return (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      children={props.buttonLabel}
      testId={props.buttonTestId}
      handleClick={props.onClickAction}
    />
  );
}